/**
 * Primeiros Passos — tudo o que a tela diz e para onde ela leva, indexado pela `key` que o
 * servidor devolve (spec #387, seções 4 e 5.2).
 *
 * O payload traz estado e medida; o texto, a rota de destino e o que cada destino exige moram
 * aqui. `RouteActionButton` com `para={variável}` não é coberto pelo teste do repositório
 * (`controle-declara-acesso` só lê `para="literal"`), e por isso `__tests__/textos.test.ts`
 * confere cada `exige` desta tabela contra a rota em `App.tsx`.
 */

import { pode, type Exigencia } from '../../authorization/pode';
import { MODULE_LABELS } from '../../types/company';
import type { Perspectiva } from '../../types/manifesto';
import type {
  CodigoDeAviso,
  ItemState,
  ModoDeAtendimento,
  OnboardingItem,
  TrackKey,
} from '../../types/onboarding';

/** Um texto que muda com a perspectiva precisa dizer as três; o que é igual para todas é string. */
export type PorPerspectiva = Record<Perspectiva, string>;
export type Textual = string | PorPerspectiva;

export function escolher(texto: Textual, perspectiva: Perspectiva): string {
  return typeof texto === 'string' ? texto : texto[perspectiva];
}

/* ─────────────── destinos ─────────────── */

/** Para onde um item leva e o que essa tela exige — a mesma declaração da rota em `App.tsx`. */
export interface Destino {
  rota: string;
  exige: Exigencia;
  rotulo: string;
}

function ir(
  rota: string,
  permissao: string,
  rotulo: string,
  perspectiva?: Perspectiva
): Destino {
  return { rota, exige: pode(permissao, perspectiva ? { perspectiva } : {}), rotulo };
}

/* ─────────────── itens ─────────────── */

export interface ItemTexto {
  rotulo: Textual | ((item: OnboardingItem) => string);
  /** Singular e plural da medida de contagem: "4 profissionais". */
  unidade?: readonly [string, string];
  /** O que se conta quando o item é por entidade: "11 de 15 empresas". */
  unidadeDoTotal?: string;
  detalhe?: Partial<Record<ItemState, Textual>>;
  /** Mesmo detalhe de pendente, para o item medido em várias empresas. */
  pendenteEmVarias?: string;
  /** A frase que substitui o botão quando só a plataforma resolve: prestador e empresa só veem o estado. */
  soPlataforma?: string;
  /** A tela de leitura: sempre segura de oferecer a quem a alcança. */
  consulta?: Destino;
  /** A tela que resolve a pendência. Ausente: o item não vira "próximo passo". */
  acao?: Destino;
  /** Com várias empresas no escopo o item aponta para a lista, não para uma empresa. */
  acaoEmLista?: Destino;
  proximo?: { titulo: (faltam?: number) => string; texto: string };
}

const empresasDe = (n: number): string => `${n} ${n === 1 ? 'empresa' : 'empresas'}`;

/** "Cadastre os setores" ou, com várias empresas faltando, "Cadastre os setores de 4 empresas". */
const cadastre = (oQue: string) => (faltam?: number) =>
  faltam ? `Cadastre ${oQue} de ${empresasDe(faltam)}` : `Cadastre ${oQue}`;

const fixo = (titulo: string) => () => titulo;

const NENHUM_AINDA = {
  agendamentos: 'Nenhum agendamento ainda.',
  recepcao: 'Nenhuma presença confirmada ainda.',
  consultas: 'Nenhuma consulta ainda.',
  cat: 'Nenhuma comunicação de acidente.',
  convocacao: 'Nenhuma convocação enviada.',
};

const EMPRESAS = ir('/companies', 'company.read', 'Abrir Empresas');

export const ITENS: Readonly<Record<string, ItemTexto>> = {
  /* install */
  risks: {
    rotulo: 'Riscos Ocupacionais',
    unidade: ['cadastrado', 'cadastrados'],
    detalhe: { pending: 'Nenhum risco cadastrado. Sem eles, nenhuma empresa aplica riscos.' },
    consulta: ir('/occupational-risks', 'occupational_risk.read', 'Abrir Riscos'),
    acao: ir('/occupational-risks/new', 'occupational_risk.create', 'Cadastrar risco'),
    proximo: {
      titulo: fixo('Cadastre os riscos ocupacionais'),
      texto: 'Sem riscos cadastrados, nenhuma empresa consegue aplicá-los. Vale para todos os clientes.',
    },
  },
  sources: {
    rotulo: 'Fontes Geradoras',
    unidade: ['cadastrada', 'cadastradas'],
    detalhe: { pending: 'Nenhuma fonte geradora cadastrada.' },
    consulta: ir('/source-generators', 'source_generator.read', 'Abrir Fontes'),
    acao: ir('/source-generators/new', 'source_generator.create', 'Cadastrar fonte'),
    proximo: {
      titulo: fixo('Cadastre as fontes geradoras'),
      texto: 'As fontes geradoras entram na aplicação de riscos de todos os clientes.',
    },
  },
  epis_epcs: {
    rotulo: 'EPIs e EPCs',
    unidade: ['cadastrado', 'cadastrados'],
    detalhe: { pending: 'Nenhum EPI ou EPC cadastrado.' },
    consulta: ir('/epis', 'epi.read', 'Abrir EPIs'),
    acao: ir('/epis/new', 'epi.create', 'Cadastrar EPI'),
    proximo: {
      titulo: fixo('Cadastre os EPIs e EPCs'),
      texto: 'Os documentos de segurança citam os equipamentos de proteção cadastrados aqui.',
    },
  },
  exams: {
    rotulo: 'Exames Ocupacionais',
    unidade: ['cadastrado', 'cadastrados'],
    detalhe: { pending: 'Nenhum exame cadastrado.' },
    consulta: ir('/occupational-exams', 'occupational_exam.read', 'Abrir Exames'),
    acao: ir('/occupational-exams/new', 'occupational_exam.create', 'Cadastrar exame'),
    proximo: {
      titulo: fixo('Cadastre os exames ocupacionais'),
      texto: 'Sem exames cadastrados, nenhuma empresa recebe sugestão de exames no agendamento.',
    },
  },
  email: {
    rotulo: 'Conta de e-mail',
    unidade: ['conta ativa', 'contas ativas'],
    detalhe: { pending: 'Nenhuma conta ativa. A plataforma não envia e-mail.' },
    consulta: ir('/settings/email', 'email.config.manage', 'Abrir e-mail'),
    acao: ir('/settings/email', 'email.config.manage', 'Configurar e-mail'),
    proximo: {
      titulo: fixo('Configure a conta de e-mail da plataforma'),
      texto:
        'Sem conta de e-mail ativa, nenhuma empresa ou clínica recebe avisos do sistema. Vale para todos os clientes.',
    },
  },
  whatsapp_global: {
    rotulo: 'WhatsApp da plataforma',
    unidade: ['instância global', 'instâncias globais'],
    detalhe: { pending: 'Nenhuma instância global conectada.' },
    consulta: ir('/whatsapp', 'whatsapp.read', 'Abrir WhatsApp'),
    acao: ir('/whatsapp', 'whatsapp.read', 'Conectar WhatsApp'),
    proximo: {
      titulo: fixo('Conecte o WhatsApp da plataforma'),
      texto: 'É por ele que a plataforma envia avisos quando a empresa ou a clínica não tem número próprio.',
    },
  },

  /* account */
  users: {
    rotulo: 'Usuários',
    unidade: ['usuário', 'usuários'],
    detalhe: { pending: 'Nenhum usuário cadastrado.' },
    consulta: ir('/users', 'user.read', 'Abrir Usuários'),
    acao: ir('/users/create', 'user.create', 'Cadastrar usuário'),
    proximo: {
      titulo: fixo('Cadastre quem vai usar o sistema'),
      texto: 'Cada pessoa entra com o próprio acesso. Comece por quem administra.',
    },
  },

  /* operation */
  provider: {
    rotulo: {
      prestador: 'Clínica cadastrada',
      empresa: 'Clínica que atende sua empresa',
      plataforma: 'Clínicas',
    },
    unidade: ['clínica', 'clínicas'],
    detalhe: { pending: 'Nenhuma clínica cadastrada.' },
    consulta: ir('/providers', 'provider.read', 'Abrir Prestadores'),
    acao: ir('/providers/create', 'provider.create', 'Cadastrar clínica'),
    proximo: {
      titulo: fixo('Cadastre a clínica'),
      texto: 'É a clínica que define onde os exames acontecem.',
    },
  },
  service_mode: {
    rotulo: 'Modo de atendimento',
    detalhe: { info: 'Define quais itens da agenda se aplicam à sua clínica.' },
    consulta: ir('/providers', 'provider.read', 'Abrir Prestadores'),
  },
  professionals: {
    rotulo: 'Profissionais',
    unidade: ['profissional', 'profissionais'],
    detalhe: { pending: 'Nenhum profissional cadastrado.' },
    consulta: ir('/professionals', 'professional.read', 'Abrir Profissionais'),
    acao: ir('/professionals/create', 'professional.create', 'Cadastrar profissional'),
    proximo: {
      titulo: fixo('Cadastre os profissionais'),
      texto: 'Sem profissionais, a clínica não tem quem atenda os exames agendados.',
    },
  },
  agenda: {
    rotulo: (item) => {
      if (item.mode === 'ordem_chegada') return 'Turnos e capacidade por período';
      if (item.mode === 'hora_marcada') return 'Grade de horários da clínica';
      if (item.mode === 'agenda_profissionais') return 'Horários disponíveis dos profissionais';
      return 'Agenda pronta para o modo de cada clínica';
    },
    unidadeDoTotal: 'clínicas',
    detalhe: {
      pending: 'O agendamento fica sem período para oferecer.',
      warn: 'O agendamento funciona, mas sem limite de vagas definido: aceita quantas pessoas vierem.',
    },
    consulta: ir('/providers', 'provider.read', 'Abrir Prestadores'),
    acao: ir('/providers', 'provider.read', 'Abrir Prestadores'),
    proximo: {
      titulo: fixo('Defina a agenda da clínica'),
      texto: 'Sem agenda, o agendamento fica sem período para oferecer.',
    },
  },

  /* client */
  company: {
    rotulo: { prestador: 'Empresas atendidas', empresa: 'Empresa', plataforma: 'Empresas' },
    unidade: ['empresa', 'empresas'],
    detalhe: { pending: 'Nenhuma empresa cadastrada.' },
    consulta: EMPRESAS,
    acao: ir('/companies/create', 'company.create', 'Cadastrar empresa'),
    proximo: {
      titulo: fixo('Cadastre a primeira empresa'),
      texto: 'Sem empresa cadastrada, não há quem agendar exames.',
    },
  },
  departments: {
    rotulo: 'Setores',
    unidade: ['setor', 'setores'],
    detalhe: { pending: 'Nenhum setor cadastrado.' },
    pendenteEmVarias: 'Empresas sem nenhum setor cadastrado.',
    consulta: ir('/departments', 'department.read', 'Abrir Setores'),
    acao: ir('/departments/create', 'department.create', 'Cadastrar setor'),
    acaoEmLista: ir('/departments', 'department.read', 'Abrir Setores'),
    proximo: {
      titulo: cadastre('os setores'),
      texto: 'Sem setor cadastrado, a empresa não consegue agendar exames.',
    },
  },
  occupations: {
    rotulo: 'Cargos',
    unidade: ['cargo', 'cargos'],
    detalhe: { pending: 'Nenhum cargo cadastrado.' },
    pendenteEmVarias: 'Empresas sem nenhum cargo cadastrado.',
    consulta: ir('/occupations', 'occupation.read', 'Abrir Cargos'),
    acao: ir('/occupations/create', 'occupation.create', 'Cadastrar cargo'),
    acaoEmLista: ir('/occupations', 'occupation.read', 'Abrir Cargos'),
    proximo: {
      titulo: cadastre('os cargos'),
      texto: 'Sem cargo cadastrado, o exame do funcionário não tem como ser definido.',
    },
  },
  employees: {
    rotulo: 'Funcionários',
    unidade: ['funcionário', 'funcionários'],
    detalhe: { pending: 'Nenhum funcionário ativo na sua empresa.' },
    pendenteEmVarias: 'Empresas sem nenhum funcionário ativo.',
    consulta: ir('/employees', 'employee.read', 'Abrir Funcionários'),
    acao: ir('/employees/new', 'employee.create', 'Cadastrar funcionário'),
    acaoEmLista: ir('/employees', 'employee.read', 'Abrir Funcionários'),
    proximo: {
      titulo: cadastre('os funcionários'),
      texto: 'Sem funcionários cadastrados, não há quem agendar. Comece por um e complete os demais depois.',
    },
  },
  company_provider: {
    rotulo: 'Vínculo entre empresa e clínica',
    unidade: ['empresa', 'empresas'],
    detalhe: { pending: 'Nenhuma clínica vinculada à empresa.' },
    pendenteEmVarias: 'Empresas sem clínica vinculada.',
    consulta: EMPRESAS,
    acao: EMPRESAS,
    proximo: {
      titulo: (faltam) =>
        faltam ? `Vincule ${empresasDe(faltam)} a uma clínica` : 'Vincule a empresa a uma clínica',
      texto: 'Sem clínica vinculada, a empresa não encontra onde agendar os exames.',
    },
  },
  units: {
    rotulo: 'Unidades',
    unidadeDoTotal: 'empresas',
    detalhe: { optional: 'Opcional. Sem unidade, vale o endereço da própria empresa.' },
    consulta: ir('/units', 'unit.read', 'Abrir Unidades'),
  },

  /* risk */
  risk_applications: {
    rotulo: 'Riscos aplicados',
    soPlataforma: 'Quem aplica os riscos é a equipe da plataforma.',
    detalhe: { pending: 'Nenhum risco aplicado.' },
    pendenteEmVarias: 'Empresas sem nenhum risco aplicado.',
    consulta: ir('/risk-applications', 'risk_application.read', 'Abrir Aplicação de Riscos', 'plataforma'),
    acao: ir('/risk-applications', 'risk_application.read', 'Aplicar riscos', 'plataforma'),
    proximo: {
      titulo: (faltam) => (faltam ? `Aplique os riscos de ${empresasDe(faltam)}` : 'Aplique os riscos'),
      texto: 'Sem riscos aplicados, a empresa fica sem sugestão de exames e sem documentos de segurança.',
    },
  },
  exam_applications: {
    rotulo: 'Exames aplicados',
    soPlataforma: 'Quem aplica os exames é a equipe da plataforma.',
    detalhe: { pending: 'Nenhum exame aplicado.' },
    pendenteEmVarias: 'Empresas sem nenhum exame aplicado.',
    consulta: ir('/exam-applications', 'exam_application.read', 'Abrir Aplicação de Exames', 'plataforma'),
    acao: ir('/exam-applications', 'exam_application.read', 'Aplicar exames', 'plataforma'),
    proximo: {
      titulo: (faltam) => (faltam ? `Aplique os exames de ${empresasDe(faltam)}` : 'Aplique os exames'),
      texto: 'Sem exames aplicados, o agendamento não sugere o que cada cargo precisa fazer.',
    },
  },
  application_groups: {
    rotulo: 'Conferir o que ficou aplicado',
    detalhe: { info: 'Mostra o resultado consolidado por empresa.' },
    consulta: ir('/application-groups', 'application_group.read', 'Abrir Grupos de Aplicação'),
  },

  /* daily */
  appointments: {
    rotulo: 'Agendamento de Exames',
    unidade: ['agendamento', 'agendamentos'],
    detalhe: { pending: NENHUM_AINDA.agendamentos, locked: NENHUM_AINDA.agendamentos },
    consulta: ir('/appointments', 'appointment.read', 'Abrir Agendamentos'),
    acao: ir('/appointments/new', 'appointment.create', 'Agendar exame'),
    proximo: {
      titulo: fixo('Agende o primeiro exame'),
      texto: 'A base está pronta. O primeiro agendamento mostra como o resto funciona.',
    },
  },
  reception: {
    rotulo: 'Recepção',
    detalhe: { pending: NENHUM_AINDA.recepcao, locked: NENHUM_AINDA.recepcao },
    consulta: ir('/reception', 'reception.read', 'Abrir Recepção'),
  },
  consultations: {
    rotulo: 'Consultas Médicas',
    detalhe: { pending: NENHUM_AINDA.consultas, locked: NENHUM_AINDA.consultas },
    consulta: ir('/medical-consultations', 'medical-records.read', 'Abrir Consultas'),
  },
  cat: {
    rotulo: 'CAT',
    detalhe: { pending: NENHUM_AINDA.cat, locked: NENHUM_AINDA.cat },
    consulta: ir('/cats', 'cat.read', 'Abrir CAT'),
  },
  convocation: {
    rotulo: 'Convocação de Exames',
    detalhe: { pending: NENHUM_AINDA.convocacao, locked: NENHUM_AINDA.convocacao },
    consulta: ir('/exam-summons', 'exam_summons.read', 'Abrir Convocações'),
  },
  esocial_config: {
    rotulo: 'Configuração do eSocial',
    detalhe: { pending: 'Medido nas empresas que contrataram eSocial.' },
    consulta: EMPRESAS,
    acao: EMPRESAS,
    proximo: {
      titulo: (faltam) =>
        faltam ? `Configure o eSocial de ${empresasDe(faltam)}` : 'Configure o eSocial da empresa',
      texto: 'Sem a configuração, os eventos do eSocial não podem ser enviados.',
    },
  },

  /* channels */
  certificates: {
    rotulo: 'Certificados Digitais',
    unidade: ['certificado', 'certificados'],
    detalhe: { pending: 'Nenhum certificado cadastrado.' },
    consulta: ir('/certificates', 'certificate.read', 'Abrir Certificados'),
  },
  holidays: {
    rotulo: 'Feriados',
    detalhe: { pending: 'Nenhum feriado cadastrado nas suas empresas.' },
    consulta: ir('/holidays', 'holiday.read', 'Abrir Feriados'),
  },
  whatsapp_entity: {
    rotulo: {
      prestador: 'WhatsApp da clínica',
      empresa: 'WhatsApp da empresa',
      plataforma: 'WhatsApp de clínicas e empresas',
    },
    unidade: ['número conectado', 'números conectados'],
    detalhe: { pending: 'Nenhum número conectado.' },
    consulta: ir('/whatsapp', 'whatsapp.read', 'Abrir WhatsApp'),
  },
};

/** Todo destino da tabela, para o teste que o confere contra `App.tsx`. */
export function todosOsDestinos(): { item: string; campo: string; destino: Destino }[] {
  const destinos: { item: string; campo: string; destino: Destino }[] = [];
  for (const [item, texto] of Object.entries(ITENS)) {
    for (const campo of ['consulta', 'acao', 'acaoEmLista'] as const) {
      const destino = texto[campo];
      if (destino) destinos.push({ item, campo, destino });
    }
  }
  return destinos;
}

/** A tela que resolve o item, já sabendo se ele é medido em várias entidades. */
export function acaoDoItem(texto: ItemTexto, item: OnboardingItem): Destino | undefined {
  const emVarias = item.total !== undefined && item.total > 1;
  return emVarias && texto.acaoEmLista ? texto.acaoEmLista : texto.acao;
}

/* ─────────────── estados ─────────────── */

/** O estado nunca depende só da cor: cada um tem texto. */
export const TEXTO_DO_ESTADO: Readonly<Record<ItemState, string>> = {
  done: 'Concluído',
  warn: 'Concluído, com ressalva',
  pending: 'Pendente',
  locked: 'Aguardando',
  info: 'Informativo',
  optional: 'Opcional',
};

export const ROTULO_DO_MODO: Readonly<Record<ModoDeAtendimento, string>> = {
  agenda_profissionais: 'Agenda dos Profissionais',
  hora_marcada: 'Hora Marcada',
  ordem_chegada: 'Ordem de Chegada',
};

/** A frase de cada aviso de ressalva (spec 3.9): sempre código que o validador já conhece. */
export const FRASE_DA_RESSALVA: Readonly<Record<string, (n: number) => string>> = {
  RISCO_SEM_CLASSIFICACAO: (n) =>
    `${n} ${n === 1 ? 'risco aplicado está' : 'riscos aplicados estão'} sem classificação.`,
  EXAME_SEM_PERIODICIDADE: (n) =>
    `${n} ${n === 1 ? 'exame sem periodicidade definida' : 'exames sem periodicidade definida'}.`,
};

/* ─────────────── trilhas ─────────────── */

export interface TrilhaTexto {
  titulo: Textual;
  /** "Libera: Agendamento de Exames" — o que a trilha destrava. */
  libera?: string;
  descricao?: Textual;
  /** Nome da cor da paleta do tema que marca a trilha. */
  acento: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'grey';
}

const AGENDAMENTO = 'Agendamento de Exames';

export const TRILHAS: Readonly<Record<TrackKey, TrilhaTexto>> = {
  install: {
    titulo: 'Instalação da plataforma',
    descricao: 'Configurado uma vez e vale para todos os clientes.',
    acento: 'grey',
  },
  account: { titulo: 'Equipe e acesso', descricao: 'Quem entra no sistema.', acento: 'grey' },
  operation: {
    titulo: 'Base da operação',
    libera: AGENDAMENTO,
    descricao: {
      prestador: 'Cada clínica segue o seu modo de atendimento.',
      empresa: 'Cada clínica segue o seu modo de atendimento.',
      plataforma: 'Cada clínica segue o modo de atendimento dela.',
    },
    acento: 'primary',
  },
  client: {
    titulo: {
      prestador: 'Clientes da clínica',
      empresa: 'Cadastro da empresa',
      plataforma: 'Clientes',
    },
    libera: AGENDAMENTO,
    descricao: {
      prestador: 'Cada empresa precisa estar completa.',
      empresa: 'O cadastro que o agendamento usa.',
      plataforma: 'Cada empresa precisa estar completa.',
    },
    acento: 'secondary',
  },
  risk: {
    titulo: 'Riscos e exames',
    libera: 'sugestão de exames no agendamento e os documentos de segurança',
    acento: 'info',
  },
  daily: { titulo: 'Operação diária', acento: 'grey' },
  channels: {
    titulo: 'Canais de aviso',
    descricao: 'Servem para notificar e assinar. Nada deixa de funcionar sem eles.',
    acento: 'grey',
  },
};

export const DESCRICAO_DA_OPERACAO_DIARIA = {
  bloqueada: 'Aparece depois que a base e os clientes estão prontos.',
  liberada: 'Mostra o que já está em uso.',
};

/* ─────────────── tela ─────────────── */

export const SUBTITULO_DA_PAGINA: Readonly<Record<Perspectiva, string>> = {
  empresa: 'O que já está pronto e o que falta para sua empresa agendar exames.',
  prestador: 'O que já está pronto e o que falta para sua clínica agendar exames.',
  plataforma: 'O que já está pronto no sistema e quais clientes ainda estão incompletos.',
};

/** Código interno do módulo → nome em português; nome já em português passa; o resto vira `undefined`. */
export const nomeDoModulo = (modulo: string | undefined): string | undefined => {
  if (!modulo) return undefined;
  const porCodigo = (MODULE_LABELS as Record<string, string>)[modulo];
  if (porCodigo) return porCodigo;
  return Object.values(MODULE_LABELS).includes(modulo) ? modulo : undefined;
};

export const AVISOS: Readonly<
  Record<CodigoDeAviso, (modulo: string | undefined) => { titulo: string; texto: string }>
> = {
  AGENDA_BY_CLINIC: () => ({
    titulo: 'A agenda é configurada pela clínica que atende sua empresa.',
    texto: 'Se faltar horário para agendar, é com ela.',
  }),
  MODULE_NOT_CONTRACTED: (modulo) => {
    const nome = nomeDoModulo(modulo);
    return {
      titulo: nome ? `${nome}:` : 'Um módulo:',
      texto:
        'sua empresa não contratou este módulo, então os passos dele não se aplicam. Para contratar, fale com quem cuida do contrato.',
    };
  },
  NO_BINDING: () => ({
    titulo: 'Sua conta ainda não está ligada a nenhuma empresa ou clínica.',
    texto: 'Peça a quem administra o sistema para fazer esse vínculo.',
  }),
};

export const AVISO_DA_PLATAFORMA = {
  titulo: 'Você está vendo todos os clientes juntos.',
  texto:
    'Os números somam a plataforma. Troque de contexto, no topo, para ver o checklist de uma empresa ou clínica só.',
};

export const ESTADOS_DA_TELA = {
  carregando: 'Carregando seus passos',
  erro: {
    titulo: 'Não carregou',
    texto: 'Não conseguimos carregar seus passos agora. Tente de novo em alguns minutos.',
    botao: 'Tentar de novo',
  },
  semVinculo: {
    titulo: 'Conta sem vínculo',
    texto:
      'Sua conta ainda não está ligada a nenhuma empresa ou clínica. Peça a quem administra o sistema para fazer esse vínculo.',
    chip: 'Nada a fazer aqui',
  },
  concluido: {
    titulo: 'Tudo concluído',
    texto:
      'Todos os passos que dependem de você estão feitos. A tela continua no menu para consulta, e o aviso da página inicial some sozinho.',
  },
};

/** A quem pedir quando o que falta não está ao alcance da pessoa. */
export const QUEM_RESOLVE: Readonly<Record<Perspectiva, string>> = {
  empresa: 'quem administra sua empresa',
  prestador: 'quem administra sua clínica ou a equipe da plataforma',
  plataforma: 'quem administra a plataforma',
};

export const FRASES = {
  proximoPasso: 'Próximo passo',
  semAcao: 'Os passos que faltam não estão ao seu alcance.',
  faltamEm: 'Faltam em:',
  maisAtrasadas: 'Mais atrasadas:',
  opcional: 'Opcional · não trava nada',
  aguardando: 'Aguardando outros blocos',
};
